import { format, isSameDay, parseISO } from "date-fns";
import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Block from '@mui/icons-material/Block';
import CachedIcon from '@mui/icons-material/Cached';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  Link,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import Tooltip from '@mui/material/Tooltip';
import { green, red } from "@mui/material/colors";
import clsx from "clsx";
import ReactLoading from 'react-loading';
import { makeStyles } from 'tss-react/mui';
import whatsBackgroundDark from "../../assets/wa-background-dark.png";
import whatsBackgroundLight from "../../assets/wa-background.png";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import GetFirstName from "../../helpers/GetFirstName";
import useSettings from '../../hooks/useSettings';
import socket from "../../hooks/useSocket";
import api from "../../services/api";
import ToastError from "../../toast/error/toastError";
import { i18n } from "../../translate/i18n";
import CalendarMessageCard from "../CalendarMessageCard";
import LightboxModal from '../LightboxModal';
import MarkdownWrapper from "../MarkdownWrapper";
import MessageOptionsMenu from "../MessageOptionsMenu";
import ShowImageModal from "../ShowImageModal";
import RenderAck from "../RenderAck";
import RenderIconFile from "../RenderIconFile";
import SchedulingCard from "../SchedulingCard";
import VcardArrayPreview from "../VcardArrayPreview";
import VcardPreview from "../VcardPreview";
import "./MessagesList.css";
import RenderAudioFile from "./RenderAudioFile";

function getUrlTicketPath() {
  var url = window.location.href;
  var partesDaURL = url.split('/');
  var codigo = partesDaURL[partesDaURL.length - 1];
  return codigo;
};

const useStyles = makeStyles()((theme) => {
  return {
    messagesListWrapper: {
      overflow: "hidden",
      position: "relative",
      display: "flex",
      flexDirection: "column",
      flexGrow: 1,
    },
    messagesList: {
      backgroundSize: '370px',
      flexGrow: 1,
      position: 'relative',
      // overflow controlado pelo Virtuoso (que tem seu próprio scroller interno).
      overflow: 'hidden',
      // minHeight: 0 é crítico em flex column — sem isso o filho com height: 100%
      // colapsa quando o conteúdo é maior que a tela.
      minHeight: 0,
      '@media (max-width: 600px)': {
        paddingBottom: "90px",
      },
      ...theme.scrollbarStyles,
    },
    circleLoading: {
      position: "absolute",
      opacity: "70%",
      top: 0,
      left: "47%",
      marginTop: 12,
    },
    messageLeftGroup: {
      fontSize: '13px',
      marginLeft: 30,
      marginRight: 20,
      marginTop: 5,
      minWidth: 100,
      maxWidth: 510,
      height: "auto",
      display: "block",
      position: "relative",
      whiteSpace: "pre-wrap",
      alignSelf: "flex-start",
      borderTopLeftRadius: 0,
      borderTopRightRadius: 8,
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
      paddingLeft: 5,
      paddingRight: 5,
      paddingTop: 5,
      paddingBottom: 0,
    },
    messageLeft: {
      fontSize: '13px',
      marginRight: 20,
      marginTop: 2,
      minWidth: 100,
      maxWidth: 510,
      height: "auto",
      display: "block",
      position: "relative",
      whiteSpace: "pre-wrap",
      alignSelf: "flex-start",
      borderTopLeftRadius: 0,
      borderTopRightRadius: 8,
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
      paddingLeft: 5,
      paddingRight: 5,
      paddingTop: 5,
      paddingBottom: 0,
    },
    quotedContainerLeft: {
      margin: "-3px -80px 6px -6px",
      overflow: "hidden",
      backgroundColor: "#f0f0f0",
      borderRadius: "7.5px",
      display: "flex",
      position: "relative",
    },
    fileContainerLeft: {
      overflow: "hidden",
      backgroundColor: "#f0f0f0",
      borderRadius: "7.5px",
      display: "flex",
      position: "relative",
    },
    quotedMsg: {
      padding: 10,
      maxWidth: 300,
      height: "auto",
      display: "block",
      whiteSpace: "pre-wrap",
      overflow: "hidden",
    },
    filedMsg: {
      padding: 10,
      maxWidth: 300,
      height: "auto",
      display: "block",
      whiteSpace: "pre-wrap",
      overflow: "hidden",
    },
    quotedSideColorLeft: {
      flex: "none",
      width: "4px",
      backgroundColor: "#6bcbef",
    },
    filedSideColorLeft: {
      flex: "none",
      width: "4px",
      backgroundColor: "#6bcbef",
    },
    messageRight: {
      fontSize: '13px',
      marginLeft: 20,
      marginTop: 2,
      minWidth: 100,
      maxWidth: 610,
      height: "auto",
      display: "block",
      position: "relative",
      whiteSpace: "pre-wrap",
      alignSelf: "flex-end",
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 0,
      paddingLeft: 5,
      paddingRight: 5,
      paddingTop: 5,
      paddingBottom: 0,
    },
    quotedContainerRight: {
      width: '100%',
      margin: "-3px -80px 6px -6px",
      overflowY: "hidden",
      backgroundColor: "#cfe9ba",
      borderRadius: "7.5px",
      display: "flex",
      position: "relative",
    },
    fileContainerRight: {
      overflowY: "hidden",
      backgroundColor: "#cfe9ba",
      borderRadius: "7.5px",
      display: "flex",
      position: "relative",
    },
    quotedMsgRight: {
      padding: 10,
      maxWidth: 300,
      height: "auto",
      whiteSpace: "pre-wrap",
    },
    fileMsgRight: {
      padding: 10,
      maxWidth: 300,
      height: "auto",
      whiteSpace: "pre-wrap",
    },
    quotedSideColorRight: {
      flex: "none",
      width: "4px",
      backgroundColor: "#35cd96",
    },
    fileSideColorRight: {
      flex: "none",
      width: "4px",
      backgroundColor: "#35cd96",
    },
    messageContactName: {
      display: "flex",
      color: "#6bcbef",
      fontWeight: 500,
    },
    textContentItem: {
      overflowWrap: "break-word",
      padding: "3px 80px 6px 6px",
    },
    textContentItemEdited: {
      overflowWrap: "break-word",
      padding: "6px 0px 0px 6px",
    },
    textContentItemVcard: {
      overflowWrap: "break-word",
      padding: "3px 6px 6px 6px",
    },
    textContentItemDeleted: {
      fontStyle: "italic",
      color: "rgba(0, 0, 0, 0.36)",
      overflowWrap: "break-word",
      padding: "3px 80px 6px 6px",
    },
    messageMedia: {
      objectFit: "cover",
      width: 250,
      height: 200,
      paddingBottom: '5px',
      borderTopLeftRadius: 8,
      borderTopRightRadius: 8,
      borderBottomLeftRadius: 8,
      borderBottomRightRadius: 8,
    },
    timestamp: {
      fontSize: 11,
      position: "absolute",
      bottom: 0,
      right: 5,
      color: "#999",
    },
    timestampEdited: {
      fontSize: 11,
      bottom: 0,
      right: 5,
      color: "#999",
    },
    dailyTimestamp: {
      alignItems: "center",
      textAlign: "center",
      alignSelf: "center",
      width: "140px",
      margin: "10px",
      borderRadius: "10px",
    },
    dailyTimestampText: {
      borderRadius: '10px',
      padding: 3,
      alignSelf: "center",
      marginLeft: "0px",
    },
    ackIcons: {
      fontSize: 18,
      verticalAlign: "middle",
      marginLeft: 4,
    },
    deletedIcon: {
      fontSize: 18,
      verticalAlign: "middle",
      marginRight: 4,
    },
    ackDoneAllIcon: {
      color: green[500],
      fontSize: 18,
      verticalAlign: "middle",
      marginLeft: 4,
    },
    ackErrorIcon: {
      color: red[500],
      fontSize: 18,
      verticalAlign: "middle",
      marginLeft: 4,
    },
    downloadMedia: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "inherit",
      padding: '0px 0px 15px 0px',
    },

    driveIcon: {
      display: 'none',
      width: '20px',
      position: 'absolute',
      marginTop: '-38px',
      right: '10px',
    },

    scrollToBottomButton: {
      position: 'absolute',
      bottom: 20,
      right: 20,
      zIndex: 10,
      width: 42,
      height: 42,
      borderRadius: '50%',
      backgroundColor: theme.palette.mode === 'dark' ? '#202c33' : '#ffffff',
      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.2s ease-in-out',
      opacity: 0,
      transform: 'scale(0.8)',
      pointerEvents: 'none',
      '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2a3942' : '#f0f0f0',
        transform: 'scale(1.05)',
      },
      '&.visible': {
        opacity: 1,
        transform: 'scale(1)',
        pointerEvents: 'auto',
      },
    },

    scrollToBottomIcon: {
      color: theme.palette.mode === 'dark' ? '#8696a0' : '#54656f',
      fontSize: 28,
    },
  }
});

const reducer = (state, action) => {
  console.info(action.type);

  if (action.type === "LOAD_MESSAGES") {
    const messages = action.payload;
    const newMessages = [];

    messages.forEach((message) => {
      const messageIndex = state?.findIndex((m) => m?.id === message?.id);
      if (messageIndex !== -1) {
        state[messageIndex] = message;
      } else {
        newMessages.push(message);
      }
    });

    return [...newMessages, ...state];
  }

  if (action.type === "ADD_MESSAGE") {
    const { payload: newMessage } = action;

    const messageIndex = state?.findIndex((m) => m?.id === newMessage?.id);
    const messageIndexAwait = state?.findIndex((m) => m?.awaitSendUuid === newMessage?.awaitSendUuid && newMessage?.awaitSendUuid != null);

    if (newMessage?.contact?.isGroup) {
      newMessage.ack = 1;
    }

    if (messageIndex !== -1) {
      state[messageIndex] = newMessage;
    } else if (messageIndexAwait !== -1) {
      state[messageIndexAwait] = newMessage;
    } else {
      state.push(newMessage);
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE") {
    const messageToUpdate = action.payload;

    const messageIndex = state.findIndex((m) => {
      return (m?.id === messageToUpdate?.id || (m?.awaitSendUuid && m?.awaitSendUuid === messageToUpdate?.awaitSendUuid));
    });

    if (messageIndex !== -1) {
      if (state[messageIndex].isDeleted === false) {
        state[messageIndex] = messageToUpdate;
      }
    }

    return [...state];
  }

  if (action.type === "UPDATE_MESSAGE_ACK") {
    const { messageId, ack } = action.payload;

    // Buscar mensagem pelo messageId
    const messageIndex = state.findIndex((m) => {
      // Verificar tanto pelo messageId quanto pelo id da mensagem
      return m?.messageId === messageId || m?.id === messageId;
    });

    console.info(`[UPDATE_MESSAGE_ACK] Procurando messageId: ${messageId}, encontrado no índice: ${messageIndex}`);

    if (messageIndex !== -1) {
      // Criar nova instância do estado para evitar mutação
      const newState = [...state];

      // Atualizar o ACK da mensagem encontrada
      newState[messageIndex] = {
        ...newState[messageIndex],
        ack: ack || (newState[messageIndex].ack < 4 ? newState[messageIndex].ack + 1 : 4) // Incrementa o ack ou usa o valor fornecido
      };

      console.info(`[UPDATE_MESSAGE_ACK] Mensagem ${messageId} atualizada. ACK anterior: ${state[messageIndex].ack}, novo ACK: ${newState[messageIndex].ack}`);

      return newState;
    } else {
      console.warn(`[UPDATE_MESSAGE_ACK] Mensagem com ID ${messageId} não encontrada no estado`);
      return state;
    }
  }

  if (action.type === "RESET") {
    return [];
  }
};

// Função para filtrar duplicatas movida para fora do componente (otimização)
const removeDuplicates = (arr) => {
  const seen = new Map();
  return arr.filter((item) => {
    const key = JSON.stringify(item);
    return seen.has(key) ? false : seen.set(key, true);
  });
};

const checkIfUuidExists = (uuidToCheck, uuidArray) => {
  return uuidArray.some(uuid => uuid === uuidToCheck);
};

const VIRTUOSO_START_INDEX = 100000;

const MessagesList = ({ ticket, ticketId, isGroup, isPreview = false }) => {
  const { classes } = useStyles();
  const { themeMode } = useSettings();
  const { user } = useContext(AuthContext);
  const theme = useTheme();
  const [messagesArray, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const [contactUUIDS, setContactUUIDS] = useState([]);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Virtuoso: ref para imperativos (scroll) + firstItemIndex para manter
  // a posição visual ao carregar mensagens antigas via prepend.
  const virtuosoRef = useRef(null);
  const [firstItemIndex, setFirstItemIndex] = useState(VIRTUOSO_START_INDEX);
  const previousMessagesLength = useRef(0);

  const urlTicketPath = getUrlTicketPath();
  const effectiveUuid = ticket?.uuid || urlTicketPath; // Prioritiza a prop ticket.uuid se disponível (caso de uso em modal)

  const isIOS = useMemo(() => navigator?.userAgent?.match(/iPhone|iPad|iPod/i) || false, []);

  // Memorizar a lista de mensagens filtrada e sem duplicatas
  const messagesList = useMemo(() => {
    return removeDuplicates(
      messagesArray.filter((message) => {
        const isMatchingUuid = message.ticket.uuid === effectiveUuid;
        const uuidExistsInContact = checkIfUuidExists(message.ticket.uuid, contactUUIDS);
        return isMatchingUuid || uuidExistsInContact;
      })
    );
  }, [messagesArray, effectiveUuid, contactUUIDS]); // Usar effectiveUuid aqui

  // Detecta prepend (paginação carregou mensagens antigas) comparando o id da
  // primeira mensagem. Apenas nesse caso decrementamos firstItemIndex do
  // Virtuoso — mantendo a posição visual estável. Para append (nova mensagem
  // vinda do socket), nada precisa ser feito.
  const previousFirstId = useRef(null);
  useEffect(() => {
    const prevLen = previousMessagesLength.current;
    const currLen = messagesList.length;
    const currFirstId = currLen > 0 ? messagesList[0]?.id : null;

    if (currLen === 0 && prevLen !== 0) {
      setFirstItemIndex(VIRTUOSO_START_INDEX);
    } else if (
      currLen > prevLen &&
      prevLen > 0 &&
      previousFirstId.current !== null &&
      currFirstId !== previousFirstId.current
    ) {
      const prepended = currLen - prevLen;
      setFirstItemIndex((prev) => Math.max(0, prev - prepended));
    }

    previousMessagesLength.current = currLen;
    previousFirstId.current = currFirstId;
  }, [messagesList]);

  // Memorizar o estilo do tema
  const backgroundStyle = useMemo(() => {
    return themeMode === 'dark'
      ? { backgroundImage: `url("${whatsBackgroundDark}")` }
      : { backgroundImage: `url("${whatsBackgroundLight}")` };
  }, [themeMode]);

  // Callback para edição de mensagem (memoizado)
  const isEdited = useCallback((message) => {
    if (message.isEdited) {
      if (message.user && message.user.name) {
        return (
          <Tooltip title={message.user.name}>
            <i>{i18n.t("messagesList.others.editedMessage")}&nbsp;{`(${GetFirstName(message.user.name)})`}&nbsp;&nbsp;</i>
          </Tooltip>
        );
      } else {
        return (
          <i>{i18n.t("messagesList.others.editedMessage")}&nbsp;&nbsp;</i>
        );
      }
    }
    return '';
  }, []);

  // Mensagem apagada continua sendo exibida — o aviso vem JUNTO do conteúdo,
  // não no lugar dele. Antes só o balão enviado mostrava o ícone, então o caso
  // mais comum (o cliente apagando o que mandou) não avisava nada.
  const renderDeleted = useCallback((message) => {
    if (!message?.isDeleted) return null;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 4, opacity: 0.7, marginBottom: 2 }}>
        <Block color="disabled" fontSize="small" className={classes.deletedIcon} style={{ marginRight: 0 }} />
        <i style={{ fontSize: 12 }}>Esta mensagem foi apagada</i>
      </div>
    );
  }, [classes]);

  // Reações ficam NA mensagem reagida (coluna `reactions`, JSON de
  // `{ from, emoji, fromMe, timestamp }`) — não como um balão solto com o
  // emoji. Mesmo emoji de várias pessoas vira um chip só, com contador.
  const renderReactions = useCallback((message) => {
    let lista = message?.reactions;
    if (typeof lista === "string") {
      try { lista = JSON.parse(lista); } catch { lista = []; }
    }
    if (!Array.isArray(lista) || lista.length === 0) return null;

    const porEmoji = new Map();
    lista.forEach((r) => {
      if (!r?.emoji) return;
      porEmoji.set(r.emoji, (porEmoji.get(r.emoji) || 0) + 1);
    });
    if (porEmoji.size === 0) return null;

    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4 }}>
        {[...porEmoji.entries()].map(([emoji, count]) => (
          <span
            key={emoji}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
              padding: "0 6px",
              borderRadius: 999,
              fontSize: 12,
              lineHeight: "18px",
              background: "rgba(128,128,128,0.18)",
            }}
          >
            {emoji}
            {count > 1 && <b style={{ fontSize: 10 }}>{count}</b>}
          </span>
        ))}
      </div>
    );
  }, []);

  // Reset apenas quando o ticketId muda
  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setContactUUIDS([]);
  }, [ticketId]);

  // Scroll otimizado via Virtuoso (em vez de manipular scrollTop direto).
  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (virtuosoRef.current) {
        virtuosoRef.current.scrollToIndex({
          index: 'LAST',
          align: 'end',
          behavior: 'auto',
        });
      }
    });
  }, []);

  // Mostrar/esconder botão de "voltar ao fim" baseado no estado do Virtuoso.
  const handleAtBottomStateChange = useCallback((atBottom) => {
    setShowScrollButton(!atBottom);
  }, []);

  // Handler para clicar no botão de scroll
  const handleScrollToBottomClick = useCallback(() => {
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: 'LAST',
        align: 'end',
        behavior: 'smooth',
      });
    }
    setShowScrollButton(false);
  }, []);

  // Memorizar as mídias das mensagens
  const messagesMedia = useMemo(() => {
    const mediaMessages = [];
    messagesList.forEach(msg => {
      if (msg?.mediaType === 'imageMessage' || msg?.mediaType === 'image' ||
        msg?.mediaType === 'sticker' || msg?.mediaType === 'stickerMessage') {
        mediaMessages.push(msg);
      }
    });
    return mediaMessages;
  }, [messagesList]);

  // Otimização das chamadas de API - evita chamadas desnecessárias
  useEffect(() => {
    // Verificações de segurança no início
    if (!ticketId || (typeof ticketId !== 'string' && typeof ticketId !== 'number')) return;
    // Usa comparação com conversão de tipo para evitar problemas com string vs number
    if (String(ticket?.id) !== String(ticketId)) return;

    let isCancelled = false;

    const loadData = async () => {
      setLoading(true);
      try {
        // Carregar mensagens
        const messagesResponse = await api.get(`/messages/${ticketId}`, { params: { pageNumber } });

        if (isCancelled) return;

        // Só busca UUIDs na primeira página
        if (pageNumber === 1) {
          try {
            const uuidsResponse = await api.get(`/tickets_uuid/${ticket.contactId}/${ticketId}`);
            if (!isCancelled) {
              setContactUUIDS(uuidsResponse.data);
            }
          } catch (uuidErr) {
            console.warn('Erro ao buscar UUIDs:', uuidErr);
          }
        }

        if (isCancelled) return;

        dispatch({ type: 'LOAD_MESSAGES', payload: messagesResponse.data.messages });
        setHasMore(messagesResponse.data.hasMore);

        if (pageNumber === 1) {
          scrollToBottom();
        }
      } catch (err) {
        if (!isCancelled) {
          ToastError(err);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    // Executa imediatamente para paginação (pageNumber > 1), com delay apenas para carga inicial
    if (pageNumber === 1) {
      const timeoutId = setTimeout(loadData, 200);
      return () => {
        isCancelled = true;
        clearTimeout(timeoutId);
      };
    } else {
      // Para paginação, executa imediatamente
      loadData();
      return () => {
        isCancelled = true;
      };
    }
  }, [pageNumber, ticketId, ticket.contactId, ticket.id, scrollToBottom]);

  // Socket listeners otimizados
  useEffect(() => {
    if (String(ticketId) !== String(ticket?.id)) return;

    const handleConnect = () => {
      socket.emit("joinChatBox", `${ticket.id}`);
    };

    const handlePreMessage = (data) => {
      if (data?.uuid !== effectiveUuid) return; // Usar effectiveUuid

      const dateActual = new Date().toISOString();
      const message = {
        id: `await_${data?.messageUUID}`,
        ack: 0,
        body: data.body,
        fromMe: true,
        quotedMsg: data?.reqBody?.quotedMsg,
        timestamp: String(Date.now()),
        isDeleted: false,
        mediaName: null,
        mediaType: data?.mediaType ?? "extendedTextMessage",
        mediaUrl: data?.mediaUrl ?? null,
        participant: "",
        ticket: data.ticket,
        createdAt: dateActual,
        updatedAt: dateActual,
        awaitSendUuid: data?.messageUUID,
        mediaId: data?.mediaId ?? null,
        media: data.media ?? null,
      };
      dispatch({ type: "ADD_MESSAGE", payload: message });
      scrollToBottom();
    };

    const handleAppMessage = (data) => {
      // Verifica se os dados são uma string e precisam ser parseados
      let parsedData;
      if (typeof data === 'string') {
        try {
          parsedData = JSON.parse(data);
        } catch (error) {
          console.error('Erro ao parsear dados do appMessage:', error);
          return;
        }
      } else {
        parsedData = data;
      }

      const { ticketId: dataTicketId, ticket: { id: IdInTicket, uuid } = {}, action, message } = parsedData || {};

      console.warn('handleAppMessage dados recebidos:', { dataTicketId, IdInTicket, uuid, action, message });

      if (effectiveUuid !== uuid) { // Usar effectiveUuid
        console.warn('UUID não coincide:', { effectiveUuid, uuid });
        return;
      }

      const isTicketIdValid = !!dataTicketId && +dataTicketId === +ticket?.id;
      const isIdInTicketValid = !!IdInTicket && +IdInTicket === +ticket?.id;
      console.warn('Validação dos IDs:', { isTicketIdValid, isIdInTicketValid });

      if (isTicketIdValid || isIdInTicketValid) {
        if (action === "create") {
          dispatch({ type: "ADD_MESSAGE", payload: message });
          scrollToBottom();
        } else if (action === "update") {
          dispatch({ type: "UPDATE_MESSAGE", payload: message });
        }
      }
    };

    const handleAckMessage = (data) => {
      const { ticketId: dataTicketId, messageId, ack } = data || {};

      const isTicketIdValid = !!dataTicketId && +dataTicketId === +ticket?.id;

      if (isTicketIdValid) {
        // ✅ Passar objeto com messageId e ack no payload
        dispatch({
          type: "UPDATE_MESSAGE_ACK",
          payload: {
            messageId: messageId,
            ack: ack
          }
        });
      }
    };

    socket.on("connect", handleConnect);
    if (ticket?.id) {
      socket.on(`preMessage-${ticket.id}`, handlePreMessage);
    }
    socket.on(`appMessage-${ticket.companyId}`, handleAppMessage);
    socket.on(`appMessage-${ticket.companyId}-ack`, handleAckMessage);

    // Cleanup function para remover listeners.
    // O closure captura ticket.id e ticket.companyId desta execução do effect,
    // garantindo que o off() use as mesmas chaves que o on() usou ao montar.
    return () => {
      // socket.off("connect", handleConnect);
      if (ticket?.id) {
        socket.off(`preMessage-${ticket.id}`, handlePreMessage);
      }
      // socket.off(`appMessage-${ticket.companyId}`, handleAppMessage);
      // socket.off(`appMessage-${ticket.companyId}-ack`, handleAckMessage);
    };
  }, [ticket.id, ticketId, user.companyId, effectiveUuid]); // Adicionado effectiveUuid nas dependências

  const loadingMore = useCallback(() => {
    setPageNumber((prevPageNumber) => prevPageNumber + 1);
  }, []);

  const handleOpenMessageOptionsMenu = useCallback((e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  }, []);

  const handleCloseMessageOptionsMenu = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const [openLightbox, setOpenLightbox] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);

  const handleOpenLightbox = useCallback((blob, url, isDrive) => {
    try {
      let findUrl;

      if (isDrive || blob?.media) {
        findUrl = messagesMedia.findIndex(
          (message) => message?.driveUrl === url || message?.media?.mediaUrl === url
        );
      } else {
        findUrl = messagesMedia.findIndex(
          (message) => message.mediaUrl?.includes(url)
        );
      }

      if (findUrl === -1) {
        console.error('URL não encontrada nos dados fornecidos.');
        toastError('Erro: Não foi possível localizar a mídia solicitada.');
        return;
      }

      setOpenLightbox(true);
      setSelectedImage(findUrl);
    } catch (error) {
      console.error('Erro ao tentar abrir o lightbox:', error);
      toastError('Ocorreu um erro ao tentar abrir a mídia. Tente novamente.');
    }
  }, [messagesMedia]);

  const getColorToChatQuoted = useCallback((fromMe, themeColor, isSchedule = false) => {
    let color = '';
    if (fromMe) {
      if (themeColor === 'dark') { color = isSchedule ? '#212b36' : '#025144' }
      else { color = isSchedule ? '#fff' : '#cfe9ba' }
    } else {
      if (themeColor === 'dark') { color = '#1d282f' }
      else { color = '#f0f0f0' }
    }
    return `${color} !important`;
  }, []);

  const getColorToChatFile = useCallback((fromMe, themeColor) => {
    let color = '';
    if (fromMe) {
      if (themeColor === 'dark') { color = '#fff' }
      else { color = '#212b36' }
    } else {
      if (themeColor === 'dark') { color = '#fff' }
      else { color = '#212b36' }
    }
    return `${color} !important`;
  }, []);

  const formatLocationInfos = (message) => {
    let data;
    try {
      data = JSON.parse(message.dataJson);
    } catch {
      return (
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
          📍 Localização compartilhada
        </Typography>
      );
    }
    const locationData = data?.message?.[message.mediaType];

    if (!locationData) {
      return (
        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
          📍 Localização compartilhada
        </Typography>
      );
    }

    const {
      degreesLatitude,
      degreesLongitude,
      name,
      address,
      url,
    } = locationData;

    let mapsUrl = '';
    if (typeof degreesLatitude === 'number' && typeof degreesLongitude === 'number') {
      mapsUrl = `https://maps.google.com/?q=${degreesLatitude},${degreesLongitude}`;
    }

    return (
      <Box sx={{ width: '100%', mt: 1 }}>
        {/* Nome do estabelecimento/local */}
        {name && (
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 0.5,
              fontSize: '1.1rem'
            }}
          >
            📍 {name}
          </Typography>
        )}

        {/* Endereço */}
        {address && (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
              mb: 1,
              lineHeight: 1.4,
              fontSize: '0.875rem'
            }}
          >
            {address}
          </Typography>
        )}

        {/* Coordenadas */}
        {typeof degreesLatitude === 'number' && typeof degreesLongitude === 'number' && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              fontFamily: 'monospace',
              fontSize: '0.75rem',
              display: 'block',
              mb: 1
            }}
          >
            {degreesLatitude.toFixed(6)}, {degreesLongitude.toFixed(6)}
          </Typography>
        )}

        {/* Links */}
        <Stack direction="column" spacing={0.5} sx={{ mt: 1 }}>
          {mapsUrl && (
            <Link
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              🗺️ Ver no Google Maps
            </Link>
          )}

          {url && url !== mapsUrl && (
            <Link
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: 'primary.main',
                textDecoration: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              🌐 Visitar site
            </Link>
          )}
        </Stack>
      </Box>
    );
  };

  const renderFile = useCallback((message) => {
    const fileName = message?.media?.mediaName || message?.fileName || (message.mediaUrl ? message.mediaUrl.split("/").pop() : null);
    const fileType = fileName ? fileName.split(".").pop() : null;
    // Tipo que a Cloud API não aceita anexar (.xml, .zip, .csv…): o cliente
    // recebeu um BOTÃO de download, não o arquivo no balão. Sem sinalizar, o
    // atendente vê o card normal e assume que anexou.
    let sentAsLink = false;
    try { sentAsLink = Boolean(JSON.parse(message.dataJson || '{}')?.sentAsLink); } catch { sentAsLink = false; }

    return (
      <Box component="div"
        sx={{ backgroundColor: (theme) => getColorToChatQuoted(message.fromMe, theme.palette.mode, message.mediaType === "schedule") }}
        className={clsx(classes.fileContainerLeft, {
          [classes.fileContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.fileSideColorLeft, {
            [classes.fileSideColorRight]: message?.fromMe,
          })}
        ></span>
        <Box component="div" className={classes.fileMsg} >
          <Box component="span">
            {sentAsLink && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 5px 0', opacity: 0.75, fontSize: 11 }}>
                <span role="img" aria-label="link">🔗</span>
                <span>enviado como link de download</span>
              </div>
            )}
            <div style={{ display: 'flex', width: '255px', padding: '5px', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div>{
                  <RenderIconFile type={fileType}
                    style={{
                      width: '45px',
                      height: '40px'
                    }}
                  />
                }</div>
                <div>
                  <p
                    style={{
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      width: '140px'
                    }}
                  >{fileName || message.body}</p>
                  <small>{fileType}</small>
                </div>
                <div>
                </div>
              </div>
              <div>
                <IconButton
                  sx={{ color: (theme) => getColorToChatFile(message.fromMe, theme.palette.mode) }}
                  variant="text"
                  target="_blank"
                  href={message?.media ? message.media.mediaUrl : message?.isDrive ? message.driveUrl : message.mediaUrl}
                >
                  <DownloadForOfflineIcon />
                </IconButton >
              </div>
            </div>
          </Box>
        </Box>
      </Box >
    );
  }, [classes, getColorToChatQuoted, getColorToChatFile]);

  const checkMessageMedia = useCallback((message) => {
    console.info("Entrei na verificação de midia");

    // Template oficial (HSM): não é mídia. O corpo renderizado já vem em
    // `message.body`; o `mediaUrl` é um JSON de components (não uma URL), então
    // sem este retorno cairia no ramo de arquivo e exibiria um card quebrado.
    if (message.mediaType === "template") {
      return null;
    }

    if (message.mediaType === "locationMessage" || message.mediaType === 'liveLocationMessage') {
      let data;
      try {
        data = JSON.parse(message.dataJson);
      } catch {
        return (
          <Box sx={{ p: 2, maxWidth: '450px' }}>
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
              📍 Localização compartilhada (dados indisponíveis)
            </Typography>
          </Box>
        );
      }

      const locationData = data?.message?.[message.mediaType];
      const lat = locationData?.degreesLatitude;
      const lng = locationData?.degreesLongitude;
      const hasValidCoords = lat != null && lng != null && !isNaN(lat) && !isNaN(lng);

      // Função para gerar URL do Google Maps iframe com tema
      const generateGoogleMapsEmbedUrl = (lat, lng) => {
        const isDarkMode = theme.palette.mode === 'dark';

        // Parâmetros base
        let mapUrl = `https://maps.google.com/maps?q=${lat},${lng}&t=&z=15&ie=UTF8&iwloc=&output=embed`;

        // Adicionar parâmetros de tema escuro
        if (isDarkMode) {
          mapUrl += '&style=feature:all|element:geometry|color:0x242f3e';
          mapUrl += '&style=feature:all|element:labels.text.stroke|color:0x242f3e';
          mapUrl += '&style=feature:all|element:labels.text.fill|color:0x746855';
          mapUrl += '&style=feature:administrative.locality|element:labels.text.fill|color:0xd59563';
          mapUrl += '&style=feature:poi|element:labels.text.fill|color:0xd59563';
          mapUrl += '&style=feature:poi.park|element:geometry|color:0x263c3f';
          mapUrl += '&style=feature:poi.park|element:labels.text.fill|color:0x6b9a76';
          mapUrl += '&style=feature:road|element:geometry|color:0x38414e';
          mapUrl += '&style=feature:road|element:geometry.stroke|color:0x212a37';
          mapUrl += '&style=feature:road|element:labels.text.fill|color:0x9ca5b3';
          mapUrl += '&style=feature:road.highway|element:geometry|color:0x746855';
          mapUrl += '&style=feature:road.highway|element:geometry.stroke|color:0x1f2835';
          mapUrl += '&style=feature:road.highway|element:labels.text.fill|color:0xf3d19c';
          mapUrl += '&style=feature:transit|element:geometry|color:0x2f3948';
          mapUrl += '&style=feature:transit.station|element:labels.text.fill|color:0xd59563';
          mapUrl += '&style=feature:water|element:geometry|color:0x17263c';
          mapUrl += '&style=feature:water|element:labels.text.fill|color:0x515c6d';
          mapUrl += '&style=feature:water|element:labels.text.stroke|color:0x17263c';
        }

        return mapUrl;
      };

      return (
        <div style={{
          maxWidth: '450px'
        }}>
          {hasValidCoords ? (
            <iframe
              width="100%"
              height="400"
              style={{
                border: 0,
                borderRadius: '8px',
                maxWidth: '450px'
              }}
              src={generateGoogleMapsEmbedUrl(lat, lng)}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Mapa da localização"
            />
          ) : (
            <Box sx={{
              p: 2,
              borderRadius: '8px',
              backgroundColor: 'action.hover',
              textAlign: 'center'
            }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                📍 Localização compartilhada (coordenadas indisponíveis)
              </Typography>
            </Box>
          )}
          {formatLocationInfos(message)}
        </div>
      );
    }
    if (message.mediaType == "image" || message.mediaType == "imageMessage" || message?.mediaType === 'sticker' || message?.mediaType === 'stickerMessage') {
      return <div>
        <ShowImageModal dataMessage={message} isDrive={message?.isDrive || message?.media?.mediaUrl} onOpenLightbox={handleOpenLightbox} />
      </div>;
    }
    if (message.mediaType == "audio" || message.mediaType == "audioMessage") {
      return (
        <>
          <RenderAudioFile
            themeMode={themeMode}
            srcAudio={message?.media ? message.media.mediaUrl : message?.isDrive ? message.driveUrl : message.mediaUrl}
            colors={themeMode === 'dark' ? '#202c33' : '#ffffff'}
            idDiv={Math.floor(Math.random() * 9999999999) + 1}
            avatarUser={message?.contact?.profilePicUrl}
          />
        </>
      );
    }
    if (message.mediaType === "video" || message.mediaType === "videoMessage") {
      return (<>
        <video
          className={classes.messageMedia}
          src={message?.media ? message.media.mediaUrl : message?.isDrive ? message.driveUrl : message.mediaUrl}
          controls
        />
      </>
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <div>{renderFile(message)}</div>
          </div>
        </>
      );
    }
  }, [classes, themeMode, handleOpenLightbox, renderFile]);

  const renderDailyTimestamps = useCallback((message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <Box component="div" sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#202c33' : '#fefefe', boxShadow: 2 }} className={classes.dailyTimestampText}>
            {(format(parseISO(message?.createdAt), "dd-MM-yyyy"))}
          </Box>
        </span>
      );
    }
    if (index < messagesList.length - 1) {
      let messageDay = parseISO(messagesList[index].createdAt || new Date());
      let previousMessageDay = parseISO(messagesList[index - 1]?.createdAt || new Date());

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <Box component="div" sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#202c33' : '#fefefe', boxShadow: 2 }} className={classes.dailyTimestampText}>
              {(format(parseISO(message?.createdAt), "dd-MM-yyyy"))}
            </Box>
          </span>
        );
      }
    }
    // Sem caso especial pro último item: o Virtuoso cuida do auto-scroll
    // via followOutput, então o antigo lastMessageRef ficou redundante.
    return null;
  }, [classes, messagesList]);

  const renderNumberTicket = useCallback((message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageTicket = message.ticketId;
      let previousMessageTicket = messagesList[index - 1].ticketId;

      if (messageTicket !== previousMessageTicket) {
        return (
          <div key={`ticket-${message.id}`} >
            <Divider><Chip variant="outlined" size="small" label={`#${messageTicket}`} /></Divider>
          </div>
        );
      }
    }
  }, [messagesList]);

  const renderMessageDivider = useCallback((message, index) => {
    if (index < messagesList.length && index > 0) {
      let messageUser = messagesList[index].fromMe;
      let previousMessageUser = !!messagesList[index - 1]?.fromMe;
      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  }, [messagesList]);

  // Navegar para uma mensagem por id (usado quando o usuário clica numa
  // citação/quoted message). Como o Virtuoso virtualiza, a mensagem alvo pode
  // não estar no DOM — primeiro localizamos o índice na lista e mandamos o
  // Virtuoso rolar até ele; após rolar, o getElementById passa a funcionar.
  const getMessage = useCallback((id) => {
    if (!id) return;

    const targetIndex = messagesList.findIndex((m) => String(m?.id) === String(id));
    if (targetIndex >= 0 && virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: targetIndex,
        align: 'center',
        behavior: 'smooth',
      });
      // Pequeno highlight pós-scroll para o usuário identificar a mensagem.
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.style.transition = 'background-color 0.6s ease';
          const prev = el.style.backgroundColor;
          el.style.backgroundColor = 'rgba(255, 235, 59, 0.35)';
          setTimeout(() => { el.style.backgroundColor = prev || ''; }, 1200);
        }
      }, 350);
      return;
    }

    // Fallback: se a mensagem já está renderizada (estava perto do viewport),
    // usa o comportamento clássico.
    const elmntToView = document.getElementById(id);
    if (elmntToView) {
      elmntToView.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [messagesList]);



  const renderQuotedMessage = useCallback((message) => {
    return (
      <Box component="div"
        sx={{ backgroundColor: (theme) => getColorToChatQuoted(message.fromMe, theme.palette.mode) }}
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <Box component="div" className={classes.quotedMsg} onClick={() => getMessage(message.quotedMsgId)}>
          {!message.quotedMsg?.fromMe && (
            <Box component="span" className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </Box>
          )}
          <Box component="span"><MarkdownWrapper>{message.quotedMsg?.body}</MarkdownWrapper></Box>
        </Box>
      </Box>
    );
  }, [classes, getColorToChatQuoted, getMessage]);

  const extractContact = useCallback((array) => {
    let contactNumber = "";
    let contactName = "";

    array.forEach(item => {
      const [key, value] = item.split(":");
      if (key.includes('TEL')) {
        contactNumber = value;
      }
      if (key === "FN") {
        contactName = value;
      }
    });

    return { contactName, contactNumber };
  }, []);

  const renderContact = useCallback((message) => {
    let array = [];

    if (message.body.includes('VCARD')) {
      array = message.body.split("\n");
    } else if (message.dataJson) {
      const dataJson = JSON.parse(message.dataJson);
      array = dataJson?.message?.extendedTextMessage?.text.split("\n") || [];
    }

    let contactMessage;
    if (message.mediaType == "contactsArrayMessage") {
      const contactsArray = JSON.parse(message.body);
      contactMessage = <VcardArrayPreview contacts={contactsArray} />;
    } else {
      const { contactName, contactNumber } = extractContact(array);
      contactMessage = <VcardPreview contact={contactName} numbers={contactNumber} />;
    }
    return contactMessage;
  }, [extractContact]);

  const renderSingleMessage = useCallback((index, message) => {
    if (!message) return null;
    let isContact = message?.mediaType == 'contactMessage' || message?.mediaType == 'contactsArrayMessage';
    if (!message?.fromMe) {
      return (
        <React.Fragment key={message.id}>
          {renderMessageDivider(message, index)}
          {renderNumberTicket(message, index)}
          {renderDailyTimestamps(message, index)}
          <>
            {message.mediaType == "call_log" ?
              <React.Fragment key={message.id}>
                <Divider sx={{ padding: '15px 0px' }}>
                  <Chip label={message.body} size="small" />
                </Divider>
              </React.Fragment>
              :
              <>
                {isGroup ? (
                  <>
                    <Box component="div" id={message.id} className={`${classes.messageLeftGroup} componentChat`} sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#202c33' : '#ffffff' }}>
                      <div style={{
                        position: 'absolute',
                        marginLeft: '-45px',
                        marginTop: '-5px'
                      }}>
                        <Avatar sx={{ width: 32, height: 32 }} src={message.contact?.profilePicUrl} />
                      </div>
                      <div>
                        {!isPreview && (
                          <IconButton
                            variant="contained"
                            size="small"
                            id="messageActionsButton"
                            disabled={message.isDeleted}
                            style={{
                              display: "flex",
                              position: "absolute",
                              top: -4,
                              right: 0,
                              color: "#999",
                              zIndex: 1,
                              opacity: "90%",
                            }}
                            onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                          >
                            <ArrowDropDownIcon />
                          </IconButton>
                        )}
                        <div>
                          <span className={classes.messageContactName}>
                            {message.contact?.name}
                          </span>
                        </div>
                        {message.mediaUrl && checkMessageMedia(message)}
                        <div className={!isContact ? message.isEdited ? classes.textContentItemEdited : classes.textContentItem : classes.textContentItemVcard}>
                          {renderDeleted(message)}
                          {message.quotedMsg && renderQuotedMessage(message)}
                          {message.body.includes("VCARD") && renderContact(message)}
                          {
                            !message?.mediaUrl?.includes(message.body) &&
                            !message.body?.includes("VCARD") &&
                            message.mediaType !== "audio" &&
                            message.mediaType !== "audioMessage" &&
                            <p>
                              {!(message.mediaType === "locationMessage" || message.mediaType === 'liveLocationMessage') && <MarkdownWrapper>{message.body}</MarkdownWrapper>}
                            </p>
                          }
                          {renderReactions(message)}
                          <span className={message.isEdited ? classes.timestampEdited : classes.timestamp}>
                            {isEdited(message)}{format(parseISO(message.createdAt), "HH:mm")}
                          </span>
                        </div>
                      </div>
                    </Box>
                  </>
                ) : (
                  <>
                    <Box component="div" id={message.id} className={`${classes.messageLeft} componentChat`} sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? isIOS && (message?.mediaType === "audio" || message?.mediaType == "audioMessage") ? '#3a3a3a' : '#202c33' : isIOS && (message?.mediaType === "audio" || message?.mediaType == "audioMessage") ? '#3a3a3a' : '#ffffff' }}>
                      {!isPreview && (
                        <IconButton
                          variant="contained"
                          size="small"
                          id="messageActionsButton"
                          disabled={message.isDeleted}
                          style={{
                            display: "flex",
                            position: "absolute",
                            top: -4,
                            right: 0,
                            color: "#999",
                            zIndex: 1,
                            opacity: "90%",
                          }}
                          onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                        >
                          <ArrowDropDownIcon />
                        </IconButton>
                      )}
                      {message.mediaUrl && checkMessageMedia(message)}
                      <div className={!isContact ? message.isEdited ? classes.textContentItemEdited : classes.textContentItem : classes.textContentItemVcard}>
                        {renderDeleted(message)}
                        {message.quotedMsg && renderQuotedMessage(message)}
                        {message?.body?.includes("VCARD") && renderContact(message)}
                        {
                          !message?.mediaUrl?.includes(message.body) &&
                          !message.body?.includes("VCARD") &&
                          message.mediaType !== "audio" &&
                          message.mediaType !== "audioMessage" &&
                          <p>
                            {!(message.mediaType === "locationMessage" || message.mediaType === 'liveLocationMessage') && <MarkdownWrapper>{message.body}</MarkdownWrapper>}
                          </p>
                        }
                        {renderReactions(message)}
                        <span className={message.isEdited ? classes.timestampEdited : classes.timestamp}>
                          {isEdited(message)}{format(parseISO(message.createdAt), "HH:mm")}
                        </span>
                      </div>
                    </Box>
                  </>)
                }
              </>}
          </>
        </React.Fragment >
      );
    } else {
      return (
        <React.Fragment key={message.id} >
          {renderMessageDivider(message, index)}
          {renderDailyTimestamps(message, index)}
          {message.mediaType === "notepad" ?
            <>
              <Box component="div" id={message.id} className={`${classes.messageRight} componentChat`} sx={{ fontStyle: 'italic', backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#e5e5e5' : '#feffd3' }}>
                <div
                  className={!isContact ? message.isEdited ? classes.textContentItemEdited : classes.textContentItem : classes.textContentItemVcard}
                >
                  <Box component="div" sx={{ color: (theme) => theme.palette.mode === 'dark' ? '#3c3c3c' : '#212b36' }}>
                    <p> {!(message.mediaType === "locationMessage" || message.mediaType === 'liveLocationMessage') && <MarkdownWrapper>{message.body}</MarkdownWrapper>}</p>
                  </Box>
                  <span className={message.isEdited ? classes.timestampEdited : classes.timestamp}>
                    {isEdited(message)}{format(parseISO(message.createdAt), "HH:mm")}
                    <NoteAltIcon style={{ margin: '-5px 5px', width: 20 }} />
                  </span>
                </div>
              </Box>
            </>
            : !!message.scheduleId ? (
              <Box component="div" id={message.id} className={`${classes.messageRight} componentChat`}>
                <SchedulingCard
                  scheduleId={message.schedule}
                  message={message}
                  mediaComponent={checkMessageMedia(message, true)}
                  ack={<RenderAck message={message} />}
                />
              </Box>
            )
              : !!message.calendarId ? (
                <Box component="div" id={message.id} className={`${classes.messageRight} componentChat`}>
                  <CalendarMessageCard
                    calendarId={message.calendarId}
                    message={message}
                    ack={<RenderAck message={message} />}
                  />
                </Box>
              )
                :
                <>
                  <Box component="div" id={message.id} className={`${classes.messageRight} componentChat`} sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? isIOS && (message?.mediaType === "audio" || message?.mediaType == "audioMessage") ? '#3a3a3a' : '#005c4b' : isIOS && (message?.mediaType === "audio" || message?.mediaType == "audioMessage") ? '#3a3a3a' : '#d9fdd3' }}>
                    {!isPreview && (
                      <IconButton
                        variant="contained"
                        size="small"
                        id="messageActionsButton"
                        disabled={message.isDeleted}
                        style={{
                          display: "flex",
                          position: "absolute",
                          top: -4,
                          right: 0,
                          color: "#999",
                          zIndex: 1,
                          opacity: "90%",
                        }}
                        onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                    )}
                    {(message.mediaUrl || message?.media) && checkMessageMedia(message)}
                    <div
                      className={!isContact ? message.isEdited || message?.isSendInApi ? classes.textContentItemEdited : classes.textContentItem : classes.textContentItemVcard}
                    >
                      {renderDeleted(message)}
                      {message.quotedMsg && renderQuotedMessage(message)}
                      {
                        (message?.body?.includes("VCARD") || Array.isArray(message?.dataJson) && message?.dataJson?.includes('VCARD')) && renderContact(message)
                      }
                      {
                        !message?.mediaUrl?.includes(message.body) &&
                        !message.body?.includes("VCARD") &&
                        message.mediaType !== "audio" &&
                        message.mediaType !== "audioMessage" &&
                        (!message.mediaUrl || message.mediaType === "template") &&
                        <p>
                          {!(message.mediaType === "locationMessage" || message.mediaType === 'liveLocationMessage') && <MarkdownWrapper>{message.body}</MarkdownWrapper>}
                        </p>
                      }
                      {renderReactions(message)}
                      <span className={message.isEdited || message?.isSendInApi ? classes.timestampEdited : classes.timestamp}>
                        {isEdited(message)}
                        {message?.isSendInApi && 'Via API - '}
                        {format(parseISO(message.createdAt), "HH:mm")}
                        {<RenderAck message={message} />}
                      </span>
                    </div>
                  </Box>
                </>
          }
        </React.Fragment >
      );
    }
  }, [messagesList, isGroup, classes, isIOS, handleOpenMessageOptionsMenu, checkMessageMedia, renderQuotedMessage, renderContact, isEdited, renderDeleted, renderReactions, renderMessageDivider, renderNumberTicket, renderDailyTimestamps, isPreview]);

  return (
    <>
      <LightboxModal
        id="messageMediaModal"
        images={messagesMedia}
        mainSrc={messagesMedia[selectedImage]}
        photoIndex={selectedImage}
        setPhotoIndex={setSelectedImage}
        isOpen={openLightbox}
        onCloseRequest={() => setOpenLightbox(false)}
      />
      <div className={classes.messagesListWrapper}>
        <MessageOptionsMenu
          message={selectedMessage}
          anchorEl={anchorEl}
          menuOpen={messageOptionsMenuOpen}
          handleClose={handleCloseMessageOptionsMenu}
        />

        {/* Botão de scroll para baixo estilo WhatsApp */}
        <Box
          className={`${classes.scrollToBottomButton} ${showScrollButton ? 'visible' : ''}`}
          onClick={handleScrollToBottomClick}
        >
          <KeyboardArrowDownIcon className={classes.scrollToBottomIcon} />
        </Box>

        <div
          id="messagesList"
          className={classes.messagesList}
          style={backgroundStyle}
        >
          {messagesList.length > 0 ? (
            <Virtuoso
              ref={virtuosoRef}
              data={messagesList}
              firstItemIndex={firstItemIndex}
              initialTopMostItemIndex={Math.max(messagesList.length - 1, 0)}
              followOutput="auto"
              alignToBottom
              atBottomStateChange={handleAtBottomStateChange}
              increaseViewportBy={{ top: 400, bottom: 400 }}
              style={{ height: '100%', width: '100%' }}
              components={{
                // Botão manual "Carregar mais" no topo. Só aparece quando há
                // mais histórico a buscar; durante o request mostra um spinner.
                Header: () => {
                  if (loading) {
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                        <Box
                          as={ReactLoading}
                          sx={{ fill: (t) => `${t.palette.primary.main} !important` }}
                          type={'spin'}
                          style={{ height: '32px', width: '32px' }}
                        />
                      </Box>
                    );
                  }
                  if (hasMore) {
                    return (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
                        <IconButton
                          onClick={loadingMore}
                          size="small"
                          sx={{
                            backgroundColor: (t) => t.palette.mode === 'dark' ? '#212b36' : '#ffffff',
                            boxShadow: 1,
                            '&:hover': {
                              backgroundColor: (t) => t.palette.mode === 'dark' ? '#2a3640' : '#f4f4f4',
                            },
                          }}
                          color="secondary"
                          aria-label="Carregar mensagens antigas"
                        >
                          <CachedIcon />
                        </IconButton>
                      </Box>
                    );
                  }
                  return null;
                },
                // Spacer pra última mensagem não colar no MessageInput.
                Footer: () => <div style={{ height: 10 }} />,
              }}
              itemContent={(absoluteIndex, message) => {
                // O Virtuoso passa absoluteIndex relativo a firstItemIndex.
                // Convertemos para o índice real dentro do array messagesList
                // antes de chamar o renderer (que usa o índice pra detectar
                // dividers de dia/sender/ticket).
                const relativeIndex = absoluteIndex - firstItemIndex;
                // Cada item precisa ser flex-column pra que as bolhas internas
                // respeitem o alignSelf: flex-start (esquerda) ou flex-end
                // (direita) — sem o wrapper flex, todos ficam grudados à esquerda.
                // Padding lateral substitui o antigo padding do container, que
                // foi removido pra não interferir na medição de altura do Virtuoso.
                return (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      paddingLeft: 20,
                      paddingRight: 20,
                    }}
                  >
                    {renderSingleMessage(relativeIndex, message)}
                  </div>
                );
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, color: 'text.secondary' }}>
              Diga olá ao seu novo contato!
            </Box>
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(MessagesList);